import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { countBy, isEmpty, mapValues, noop } from 'lodash-es';
import { PusherEventName } from 'anchor-scout-common/types';
import { TableList, Page, PageEmptyState, PageErrorState, Button, Typography, PageContentHeader, ConfirmationModal, DropdownMenu, ChipButton, useStateInRef } from 'seagull';
import { ChevronDown, ChevronUp, EmailSent } from 'seagull/icons';
import { useStateFromUrl } from 'seagull/utils/useStateFromUrl';
import AgreementStudioPage from '~/components/AgreementStudioPage/AgreementStudioPage';
import { useFetchAgreementStudioData } from '~/components/AgreementStudioPage/hooks/useFetchAgreementStudioData';
import { CreateProposalButton } from '~/components/CreateProposalButton/CreateProposalButton';
import ExternalLinkButton from '~/components/ExternalLinkButton';
import PageLoadingState from '~/components/PageLoadingState';
import TableFilters from '~/components/TableFilters/TableFilters';
import { SearchFilterType } from '~/components/TableFilters/types';
import { ErrorText, routes } from '~/constants';
import { useOverlayContext, useStore, useStoreActions, useStoreStatic } from '~/contexts';
import { useDebouncedLogSearchToBI } from '~/hooks/useDebouncedLogSearchToBI';
import { useInfiniteScroll } from '~/hooks/useInfiniteScroll';
import { useIsMobileForHomePage } from '~/hooks/useIsMobileForHomePage';
import { useProposalActions } from '~/hooks/useProposalActions';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useSortFromUrl } from '~/hooks/useSortFromUrl';
import { useBulkActionModal } from '~/hooks/useTerminateAgreement/useBulkActionModal';
import { Intent } from '~/infra/access-control';
import { handlePromise } from '~/infra/utils';
import { relationshipSummaryModel } from '~/models';
import { DraftModel } from '~/models/DraftModel';
import { RelationshipStatus } from '~/models/relationship';
import { BusinessChannelEventBinder } from '~/pusher';
import { selectors } from '~/store/state-selectors';
import pluralize from '~/utils/pluralize';
import SeeWhatItsLike from '../../components/SeeWhatItsLike/SeeWhatItsLike';
import ActivityLogModal from './ActivityLogModal/ActivityLogModal';
import styles from './ProposalsDashboard.module.scss';
import { sortFunctions, getStatusUIData, getProposalsStatusCounters, proposalStatusDefinition } from './ProposalsDashboard.utils';
import ProposalsDashboardRow from './ProposalsDashboardRow';
import { ProposalsIllustration } from './ProposalsIllustration';
import img1 from './SeeWhatItsLikeImages/1.png';
import img2 from './SeeWhatItsLikeImages/2.png';
import img3 from './SeeWhatItsLikeImages/3.png';
import img4 from './SeeWhatItsLikeImages/4.png';
import { WithdrawModal } from './WithdrawModal/WithdrawModal';
const vendorTitle = 'Sent proposals';
const clientTitle = 'Proposals';
export var HeaderColId = /*#__PURE__*/ function(HeaderColId) {
    HeaderColId["CompanyName"] = "companyName";
    HeaderColId["SentDate"] = "sentDate";
    HeaderColId["SentBy"] = "sentBy";
    HeaderColId["Status"] = "status";
    return HeaderColId;
}({});
var FilterType = /*#__PURE__*/ function(FilterType) {
    FilterType["Status"] = "status";
    FilterType["SentBy"] = "sentBy";
    return FilterType;
}(FilterType || {});
const getTableDefs = ()=>{
    const sentDateLabel = 'Sent date';
    const sentByLabel = 'Sent by';
    return [
        {
            headerItem: {
                id: "companyName",
                value: 'Company name'
            },
            columnDef: {
                width: 300,
                sortable: true,
                mobile: {}
            },
            defaultSortDirection: 'desc'
        },
        {
            headerItem: {
                id: "sentDate",
                value: sentDateLabel
            },
            columnDef: {
                width: [
                    90,
                    110
                ],
                sortable: true,
                mobile: {
                    label: {
                        text: sentDateLabel
                    }
                }
            },
            defaultSortDirection: 'desc'
        },
        {
            headerItem: {
                id: "sentBy",
                value: sentByLabel
            },
            columnDef: {
                width: [
                    150,
                    180
                ],
                sortable: true,
                mobile: {
                    label: {
                        text: sentByLabel
                    }
                }
            },
            defaultSortDirection: 'desc'
        },
        {
            headerItem: {
                id: "status",
                value: 'Status'
            },
            columnDef: {
                width: [
                    230,
                    300
                ],
                sortable: true,
                sortOrder: [
                    'asc',
                    'desc'
                ],
                mobile: {}
            },
            defaultSortDirection: 'asc'
        }
    ];
};
const filterLabels = {
    ["status"]: 'Proposal status',
    ["sentBy"]: 'Sent by'
};
const defaultSort = {
    colIndex: 1,
    sort: 'desc'
};
export function ActualProposalsDashboard(props) {
    const { data, isRefreshing, refresh } = props;
    const isVendor = useStore(selectors.user.isVendor);
    const overlayContext = useOverlayContext();
    const { api, logBI } = useStoreActions();
    const fetchAgreementStudioData = useFetchAgreementStudioData();
    const { getState } = useStoreStatic();
    const scrollElRef = useRef(null);
    const [viewProposalMode, setViewProposalMode] = useState(null);
    const defs = useMemo(()=>getTableDefs(), []);
    const headerItems = useMemo(()=>defs.map((d)=>d.headerItem), [
        defs
    ]);
    const columnDefs = useMemo(()=>defs.map((d)=>d.columnDef), [
        defs
    ]);
    const sortIds = useMemo(()=>defs.map((d)=>d.headerItem.id), [
        defs
    ]);
    const sortOptions = useMemo(()=>defs.flatMap((d, colIndex)=>d.columnDef.sortable ? [
                {
                    id: d.headerItem.id,
                    label: String(d.headerItem.value),
                    defaultDirection: d.defaultSortDirection,
                    colIndex
                }
            ] : []), [
        defs
    ]);
    const { sort, changeSort } = useSortFromUrl({
        defaultSort,
        sortIds
    });
    const actions = useProposalActions();
    const debouncedLogSearchToBI = useDebouncedLogSearchToBI(11303);
    const { doBulkAction } = useBulkActionModal();
    const isMobileDevice = useIsMobileForHomePage();
    const { urlState: { filter, search, sentBy }, changeUrlState } = useStateFromUrl({
        filter: {
            multi: true,
            defaultValue: []
        },
        search: {
            defaultValue: ''
        },
        // people names may contain commas, so we use a custom query parameter delimeter
        sentBy: {
            multi: true,
            defaultValue: [],
            delimeter: '|'
        }
    });
    const sortedAndFiltered = useMemo(()=>{
        const textSearchProposal = (search, proposal)=>"".concat(relationshipSummaryModel.opposingPartyName(proposal, isVendor), " ").concat(proposal.title).toLowerCase().includes(search);
        const filtered = data.filter((proposal)=>{
            const passesStatusFilter = filter.length === 0 || filter.includes(getStatusUIData(proposal, isVendor).viewableStatus);
            const passesSentByFilter = sentBy.length === 0 || sentBy.includes(proposal.seller.id);
            const normalizedSearch = search ? search.trim().toLowerCase() : search;
            const passesSearch = !normalizedSearch || textSearchProposal(normalizedSearch, proposal);
            return passesStatusFilter && passesSentByFilter && passesSearch;
        });
        if (sort) filtered.sort(sortFunctions[sort.colIndex]({
            isVendor,
            isAscending: sort.sort === 'asc'
        }));
        return filtered;
    }, [
        data,
        isVendor,
        sort,
        filter,
        search,
        sentBy
    ]);
    const [selected, setSelected] = useState({});
    const getSelected = useStateInRef(selected);
    const [bulkActionsOpen, setBulkActionsOpen] = useState(false);
    const numSelected = useMemo(()=>{
        var _countBy_true;
        return (_countBy_true = countBy(selected).true) !== null && _countBy_true !== void 0 ? _countBy_true : 0;
    }, [
        selected
    ]);
    const { paginatedData, onScroll } = useInfiniteScroll({
        data: sortedAndFiltered,
        scrollElRef
    });
    const counters = useMemo(()=>getProposalsStatusCounters(data, isVendor), [
        data,
        isVendor
    ]);
    const statusOptions = useMemo(()=>{
        const statusDefinition = proposalStatusDefinition(isVendor);
        const availableStatuses = Object.keys(counters);
        return Object.keys(statusDefinition).filter((status)=>availableStatuses.indexOf(status) > -1).sort((a, b)=>statusDefinition[b].severity - statusDefinition[a].severity).map((statusName)=>({
                label: statusDefinition[statusName].statusText,
                value: statusName,
                counter: counters[statusName]
            }));
    }, [
        isVendor,
        counters
    ]);
    const sentByOptions = useMemo(()=>{
        const sellerMap = data.reduce((acc, param)=>{
            let { seller: { id, firstName, lastName } } = param;
            return firstName != null && lastName != null ? Object.assign(acc, {
                [id]: "".concat(firstName, " ").concat(lastName)
            }) : acc;
        }, {});
        return Object.entries(sellerMap).sort((param, param1)=>{
            let [, a] = param, [, b] = param1;
            return a.localeCompare(b);
        }).map((param)=>{
            let [id, name] = param;
            return {
                label: name,
                value: id
            };
        });
    }, [
        data
    ]);
    const tableFilterConfig = useMemo(()=>({
            ["status"]: {
                type: SearchFilterType.MultiSelectFilter,
                options: statusOptions,
                removable: true
            },
            ["sentBy"]: {
                type: SearchFilterType.MultiSelectFilter,
                options: sentByOptions,
                removable: true
            }
        }), [
        statusOptions,
        sentByOptions
    ]);
    const tableSelectedFilters = useMemo(()=>{
        const next = {};
        if (filter.length > 0) next["status"] = filter;
        if (sentBy.length > 0) next["sentBy"] = sentBy;
        return next;
    }, [
        filter,
        sentBy
    ]);
    const handleTableFiltersRefresh = useCallback((newFilters, newSort)=>{
        var _newFilters_status, _newFilters_sentBy;
        changeUrlState({
            filter: (_newFilters_status = newFilters["status"]) !== null && _newFilters_status !== void 0 ? _newFilters_status : [],
            sentBy: (_newFilters_sentBy = newFilters["sentBy"]) !== null && _newFilters_sentBy !== void 0 ? _newFilters_sentBy : []
        });
        if (newSort && (newSort.colIndex !== (sort === null || sort === void 0 ? void 0 : sort.colIndex) || newSort.sort !== (sort === null || sort === void 0 ? void 0 : sort.sort))) {
            changeSort(newSort);
        }
    }, [
        changeUrlState,
        changeSort,
        sort
    ]);
    const onSortChange = (sort)=>{
        logBI(11300, {
            column: sort ? headerItems[sort.colIndex].id : 'none'
        });
        changeSort(sort);
    };
    const resetFilters = ()=>{
        changeUrlState({
            filter: [],
            search: '',
            sentBy: []
        });
    };
    useEffect(()=>{
        // this unselects any agreement that is not found in the newly fetched list of agreements
        if (!data || isEmpty(getSelected())) return;
        const ids = data.map((proposal)=>proposal.id);
        const newSelected = mapValues(getSelected(), (origValue, proposalId)=>ids.includes(proposalId) ? origValue : false);
        setSelected(newSelected);
    }, [
        data
    ]);
    function getSelectedProposals() {
        return Object.entries(selected).filter((param)=>{
            let [, isSelected] = param;
            return isSelected;
        }).map((param)=>{
            let [proposalId, _] = param;
            return proposalId;
        });
    }
    const onToggleAllSelection = useCallback((selectAll)=>{
        setSelected(sortedAndFiltered.reduce((acc, param)=>{
            let { id } = param;
            return Object.assign(acc, {
                [id]: selectAll
            });
        }, {}));
    }, [
        sortedAndFiltered
    ]);
    if (!data.length) {
        return /*#__PURE__*/ _jsx(Page, {
            header: isVendor ? vendorTitle : clientTitle,
            loading: isRefreshing,
            variant: "containedHeight",
            children: /*#__PURE__*/ _jsx(PageEmptyState, {
                maxHeight: true,
                testId: "proposals-dashboard-empty",
                illustration: /*#__PURE__*/ _jsx(ProposalsIllustration, {}),
                title: isVendor && 'Create proposals in a matter of minutes',
                message: isVendor ? 'Our proposals are designed for a fast setup with smart fields, a service library, and default settings.' : 'Here you will see proposals pending your approval',
                actions: isVendor && /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(SeeWhatItsLike, {
                            testId: "proposals-dashboard",
                            steps: [
                                {
                                    title: 'Standardized proposal creation',
                                    subtitle: 'One proposal creation process for all of your services with easy client searching, a testing option, and a service library.',
                                    image: {
                                        src: img1,
                                        width: 1246,
                                        height: 748
                                    }
                                },
                                {
                                    title: 'Productized services, made easy',
                                    subtitle: 'Break down your offerings into service modules that can be mixed up for different client cases and easily added to your agreements.',
                                    image: {
                                        src: img2,
                                        width: 1246,
                                        height: 748
                                    }
                                },
                                {
                                    title: 'Get proposals approved quickly!',
                                    subtitle: 'Show your clients a branded, clear breakdown of your services with pricing so they can easily understand what they need to approve.',
                                    image: {
                                        src: img3,
                                        width: 1198,
                                        height: 700
                                    }
                                },
                                {
                                    title: 'Tracking your proposals made simple',
                                    subtitle: /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsx(Typography, {
                                                skin: "invertedStandard",
                                                children: "Keep track of the proposal status, edit, and remind a client to approve — all in one place!"
                                            }),
                                            /*#__PURE__*/ _jsx(Typography, {
                                                skin: "invertedStandard",
                                                children: "We will support you with automatic reminders as well."
                                            })
                                        ]
                                    }),
                                    image: {
                                        src: img4,
                                        width: 1246,
                                        height: 748
                                    }
                                }
                            ]
                        }),
                        /*#__PURE__*/ _jsx(CreateProposalButton, {
                            text: "Create proposal",
                            hidePrefix: true,
                            testId: "agreements-empty-new-btn"
                        })
                    ]
                })
            })
        });
    }
    const openImportProposalsModal = ()=>{
        overlayContext.showModal({
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                    textAlignCenter: false,
                    primaryButtonLabel: "Close",
                    onPrimaryButtonClick: dismissModal,
                    onCloseButtonClick: dismissModal,
                    title: "Import proposals",
                    primaryButtonTestId: "close-import-proposals-modal",
                    content: /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                children: "Need to send multiple proposals and speed up creation process?"
                            }),
                            /*#__PURE__*/ _jsxs(Typography, {
                                children: [
                                    /*#__PURE__*/ _jsx(ExternalLinkButton, {
                                        openInNewTab: true,
                                        href: "https://forms.gle/Q9sMHXFcp5Fs866FA",
                                        children: "Complete this form"
                                    }),
                                    ' ',
                                    "and our team will reach out with the next steps."
                                ]
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph2",
                                style: {
                                    marginTop: '16px'
                                },
                                children: "Please note: This service is currently available for requests of 30 proposals or more."
                            })
                        ]
                    })
                });
            }
        });
    };
    async function bulkSendReminders(selectedProposals) {
        await doBulkAction({
            bulkItemIds: selectedProposals,
            confirmation: {
                title: 'Send reminders in bulk',
                primaryButtonLabel: 'Send reminders',
                content: /*#__PURE__*/ _jsx("div", {
                    children: /*#__PURE__*/ _jsxs(Typography, {
                        children: [
                            'By selecting "Send reminders", reminders will be sent to all clients of the',
                            ' ',
                            /*#__PURE__*/ _jsx("strong", {
                                children: selectedProposals.length
                            }),
                            " selected proposals."
                        ]
                    })
                })
            },
            progressTitle: 'Send reminders in bulk',
            actionName: 'sent',
            itemName: 'reminder',
            onConfirmed: ()=>{
                logBI(11255, {
                    count: selectedProposals.length
                });
            },
            canProcessItem: ()=>true,
            processSingleItem: (proposalId)=>handlePromise(api.relationship.sendProposalApprovalReminder(proposalId)),
            onDone: (failedProposalIds)=>{
                setSelected(failedProposalIds.reduce((acc, proposalId)=>_object_spread_props(_object_spread({}, acc), {
                        [proposalId]: true
                    }), {}));
            }
        });
    }
    function viewActivityLog(proposal) {
        overlayContext.showModal({
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ActivityLogModal, {
                    relationshipId: proposal.id,
                    dismissModal: dismissModal
                });
            }
        });
    }
    async function viewProposal(proposal) {
        const worksAt = selectors.user.business(getState());
        const userEmail = selectors.user.email(getState());
        if (!worksAt || !userEmail) return;
        const loader = 'viewProposal';
        overlayContext.startLoader(loader);
        const result = await handlePromise(Promise.all([
            api.relationship.get(proposal.id),
            fetchAgreementStudioData()
        ]));
        overlayContext.stopLoader(loader);
        if (result.success) {
            const [relationship] = result.data;
            const proposalAsDraft = DraftModel.fromRelationship(relationship);
            setViewProposalMode({
                proposalAsDraft,
                duplicate: actions.getDuplicateProposalAction(proposal.id, {
                    isSampleProposal: proposal.isSample,
                    draftId: proposal.draftId
                }),
                copyLink: actions.getCopyProposalLinkAction(proposal),
                sendReminder: actions.getSendProposalReminderAction(proposal.id, proposal.isSample),
                edit: actions.getEditProposalAction(proposal, 'viewProposal'),
                cancel: {
                    text: 'Withdraw',
                    intent: Intent.CancelProposal,
                    disabled: false,
                    onClick: ()=>overlayContext.showModal({
                            closeOnClickOutside: true,
                            closeOnEscapeKey: true,
                            content: (param)=>{
                                let { dismissModal } = param;
                                return /*#__PURE__*/ _jsx(WithdrawModal, {
                                    openedBy: "agreement-studio",
                                    relationshipId: relationship.id,
                                    draftId: relationship.draftId,
                                    dismissModal: dismissModal,
                                    onSuccess: refresh
                                });
                            }
                        })
                }
            });
        } else {
            overlayContext.showErrorToast(ErrorText.UnexpectedErrorTryAgain);
        }
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(BusinessChannelEventBinder, {
                source: "scout",
                eventName: PusherEventName.RelationshipCreatedFromScheduledDraft,
                callback: refresh
            }),
            /*#__PURE__*/ _jsxs(Page, {
                testId: "proposals-dashboard",
                ref: scrollElRef,
                header: isVendor ? vendorTitle : clientTitle,
                actions: isVendor && /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(Button, {
                            skin: "primaryOutline",
                            testId: "import-proposals-btn",
                            onClick: ()=>openImportProposalsModal(),
                            children: "Import"
                        }),
                        /*#__PURE__*/ _jsx(CreateProposalButton, {})
                    ]
                }),
                subheader: "".concat(pluralize('sent proposal', data.filter((proposal)=>proposal.status !== RelationshipStatus.Expired).length, true)),
                loading: isRefreshing,
                onScroll: onScroll,
                children: [
                    !!viewProposalMode && /*#__PURE__*/ _jsx(AgreementStudioPage, {
                        disableDataFetching: true,
                        viewProposalMode: viewProposalMode,
                        returnRoute: routes.proposalsDashboard,
                        onClose: ()=>setViewProposalMode(null)
                    }),
                    /*#__PURE__*/ _jsxs(TableList.Provider, {
                        hasActionColumn: true,
                        columnDefs: columnDefs,
                        sort: sort,
                        onSortChange: onSortChange,
                        enableRowSelection: isVendor,
                        selectedRows: selected,
                        onSelectionChange: setSelected,
                        numRows: sortedAndFiltered.length,
                        numSelectedRows: numSelected,
                        onToggleAllSelection: onToggleAllSelection,
                        children: [
                            /*#__PURE__*/ _jsxs(PageContentHeader, {
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: styles.filtersRow,
                                        children: [
                                            /*#__PURE__*/ _jsx(TableFilters, {
                                                alwaysShowAllFilters: true,
                                                testIdPrefix: "proposals-dashboard",
                                                search: {
                                                    value: search,
                                                    testId: 'proposals-dashboard-search',
                                                    registerHotkey: true,
                                                    onChange: (event)=>{
                                                        const searchStr = event.target.value;
                                                        changeUrlState({
                                                            search: searchStr
                                                        });
                                                        debouncedLogSearchToBI.onChange(searchStr);
                                                    },
                                                    onClear: ()=>{
                                                        changeUrlState({
                                                            search: ''
                                                        });
                                                        debouncedLogSearchToBI.onClear();
                                                    }
                                                },
                                                filterLabels: filterLabels,
                                                filterConfig: tableFilterConfig,
                                                selectedFilters: tableSelectedFilters,
                                                setSelectedFilters: noop,
                                                onRefresh: handleTableFiltersRefresh,
                                                onClear: resetFilters,
                                                isClearDisabled: !filter.length && !search && !sentBy.length,
                                                mobile: {
                                                    sortOptions
                                                }
                                            }),
                                            isVendor && !isMobileDevice && /*#__PURE__*/ _jsxs("div", {
                                                className: styles.bulkActions,
                                                children: [
                                                    /*#__PURE__*/ _jsx(Typography, {
                                                        testId: "agreement-dashboard-bulk-counter",
                                                        children: numSelected > 0 ? "".concat(pluralize('proposal', numSelected, true), " selected") : ''
                                                    }),
                                                    /*#__PURE__*/ _jsx(DropdownMenu, {
                                                        onOpenChange: setBulkActionsOpen,
                                                        target: /*#__PURE__*/ _jsx(ChipButton, {
                                                            testId: "proposals-dashboard-bulk-actions",
                                                            suffixIcon: bulkActionsOpen ? /*#__PURE__*/ _jsx(ChevronUp, {}) : /*#__PURE__*/ _jsx(ChevronDown, {}),
                                                            highlight: bulkActionsOpen,
                                                            disabled: numSelected === 0,
                                                            children: "Bulk actions"
                                                        }),
                                                        children: /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                                                            iconStart: /*#__PURE__*/ _jsx(EmailSent, {}),
                                                            testId: "proposals-dashboard-bulk-send-reminders",
                                                            onClick: ()=>bulkSendReminders(getSelectedProposals()),
                                                            disabled: !isVendor,
                                                            children: "Send reminders"
                                                        })
                                                    })
                                                ]
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(TableList.Header, {
                                        items: headerItems,
                                        testId: "proposals-dashboard-table-header"
                                    })
                                ]
                            }),
                            sortedAndFiltered.length > 0 ? /*#__PURE__*/ _jsx(_Fragment, {
                                children: paginatedData.map((proposal, index)=>/*#__PURE__*/ _jsx(ProposalsDashboardRow, {
                                        proposal: proposal,
                                        isVendor: isVendor,
                                        index: index,
                                        refresh: refresh,
                                        onViewActivityLog: viewActivityLog,
                                        onViewProposal: viewProposal,
                                        proposalActions: actions
                                    }, proposal.id))
                            }) : /*#__PURE__*/ _jsx(PageEmptyState, {
                                illustration: /*#__PURE__*/ _jsx(ProposalsIllustration, {}),
                                title: "No Proposals match your request",
                                message: "Try adjusting your search by removing filters.",
                                actions: /*#__PURE__*/ _jsx(Button, {
                                    onClick: resetFilters,
                                    children: "Remove all filters"
                                })
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
export function ProposalsDashboard() {
    const { data, loading, error } = useStore((state)=>state.proposals);
    const isVendor = useStore(selectors.user.isVendor);
    const { fetchProposals } = useStoreActions();
    useSetDocumentTitle(isVendor ? vendorTitle : clientTitle);
    useEffect(()=>{
        fetchProposals();
    }, [
        isVendor
    ]);
    if (error) {
        return /*#__PURE__*/ _jsx(PageErrorState, {
            title: isVendor ? vendorTitle : clientTitle,
            onRefresh: fetchProposals
        });
    } else if (!data) {
        return /*#__PURE__*/ _jsx(PageLoadingState, {
            header: isVendor ? vendorTitle : clientTitle,
            loading: loading
        });
    } else {
        return /*#__PURE__*/ _jsx(ActualProposalsDashboard, {
            data: data,
            isRefreshing: loading,
            refresh: fetchProposals
        });
    }
}
